import React, { useContext, useEffect, useState } from 'react';
import { useUser } from 'vl-core/hooks/useUser';
import { Button, Col, Row } from 'antd';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { useQueryClient } from '@tanstack/react-query';
import ProfileImage from 'vl-common/src/components/UI/ProfileImage/ProfileImage';
import * as actions from '@actions';
import Datestamp from '../Datestamp';
import { AppointmentContext } from '../../lib/context/Appointment';
import { useRouter } from 'vl-core/useRouter';
import styled from '@emotion/styled';
import useSystemType from 'vl-core/hooks/useSystemType';
import { useDispatch } from 'wrapped-redux';
import Card from '../Card/Card';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import { ConfirmationModal, SuccessModal, errorModal } from 'vl-common/src/components/Modals/';
import { sendToParticipants } from '../../lib/sendToParticipants';
import { UserTypeCode } from 'vl-common/src/schemas/enums/userTypeCode';
import qs from 'vl-common/src/lib/query-string';
import { baseUseClinicianListQueryKey } from './useClinicianList';
import { baseGetSlotBucketsQueryKey } from '@vl-core/getSlotBuckets';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';

const mobileBreakpoint = '600px';
const tabletBreakpoint = '1200px';

const ConfirmationContainer = styled.div`
  .confirmation-appointment {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 0 80px 0;
    max-width: 1440px;
    margin: 0 auto;

    @media (max-width: ${tabletBreakpoint}) {
      padding: 0 0 80px 0;

      h1 {
        margin-bottom: 0;
      }
    }

    .card-box {
      justify-content: space-around;
      min-height: 200px;
      box-shadow: 0 3px 6px #00000029;
      border-radius: 6px;
      padding-top: 20px;
      p {
        margin-bottom: 0.5em;
      }

      @media (max-width: ${mobileBreakpoint}) {
        padding-top: 8px;
        min-height: 120px;
        padding-bottom: 8px;
      }
    }

    .confirmation-title {
      font-weight: 600 !important;

      @media (max-width: ${mobileBreakpoint}) {
        font-size: 24px;
      }
    }

    .confirmation-list {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: 24px auto 0;
      width: auto;

      .confirmation {
        margin: 16px;
        width: 420px;

        h2 {
          font-size: 20px;
          margin-bottom: 8px;
          font-weight: bold;
        }
        @media (max-width: ${mobileBreakpoint}) {
          h2 {
            font-size: 18px;
          }
        }

        .title-row {
          display: flex;
          justify-content: center;
        }

        .data-row {
          margin-left: 16px;
          padding-right: 16px;

          .icon-col {
            padding-right: 8px;

            .icon-item {
              display: block;
              width: 45px;
              height: 45px;
            }
          }

          @media (max-width: ${mobileBreakpoint}) {
            margin-left: 8px;
            padding-right: 8px;
          }
        }
      }

      @media (max-width: ${tabletBreakpoint}) {
        margin: 8px auto 0;

        .confirmation {
          max-width: 420px;
          width: 100%;
          margin: 4px;
        }
      }

      .item-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }
    }

    .confirm-button {
      display: flex;
      align-self: end;
      flex-wrap: nowrap;
      margin-top: 20px;
      width: auto;
      margin: 24px auto 0;

      > button {
        margin: 0 5px;
      }

      @media (max-width: ${mobileBreakpoint}) {
        margin: 12px auto 0;
      }
    }
  }

  .cancel-modal,
  .confirm-modal {
    width: 750px !important;

    .ant-modal-close,
    .ant-modal-header,
    .ant-modal-footer {
      display: none;
    }

    .title {
      text-align: center;
      font-size: 22px;
      color: var(--productPrimary);
      font-weight: 700;
      margin-top: 20px;

      .gray {
        color: var(--productGray);
      }

      p {
        display: inline;
        font: Bold 30px/46px Montserrat;
        letter-spacing: 0;
        margin-bottom: 20px;
      }
    }

    .description {
      margin: 15px 0 25px 0;
      text-align: center;
    }

    .buttons {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 235px;
      margin: 0 auto;

      .grey-button {
        margin-right: 10px;
        background: var(--productGray) !important;
        color: #fff !important;
      }
    }
  }
`;

const ERROR_MAP: { [key: number]: { message: string; redirectTo?: string } } = {
  403: { message: 'You are not currently authorised to make this booking.' },
  409: {
    message: 'This appointment is no longer available. Please choose another time.',
    redirectTo: '/choose'
  },
  410: { message: 'This case appointment booking is not allowed. This may be because the case is closed.' },
  412: {
    message: 'Less than 10 minutes before the start of the appointment. Please choose another time.',
    redirectTo: '/choose'
  },
  423: { message: 'Your appointment cannot be rescheduled. This may be because you have already attended it.' },
  425: {
    message: 'You are not able to book an appointment for a date/time in the past. Please choose another time.',
    redirectTo: '/choose'
  },
  451: { message: 'You are not currently eligible to book an appointment for this case.' },
  500: { message: 'An error occurred. Please try again later.' }
};

const formatError = (
  status: number,
  pageType: string,
  systemType: 'vl-app' | 'vl-admin',
  role: UserTypeCode,
  backendMessage?: string
) => {
  pageType = pageType || 'patients';
  const message = backendMessage || ERROR_MAP[status]?.message || 'An error occurred. Please try again later.';
  const redirectTo = ERROR_MAP[status]?.redirectTo || '';
  let newRoute = `/${pageType}${redirectTo}`;
  if (systemType === 'vl-admin') {
    const route = { CLIENTADMIN: 'admin', ADMIN: 'referrer-admin', REFERRER: 'referrer' }[role];
    newRoute = `/${route}/ScheduleAppointment?${window.location.hash.split('?')[1]}`;
  }
  return {
    message,
    redirectTo: newRoute
  };
};

const getError = async (
  confirmResult: any, // multiple types
  pageType: string,
  systemType: 'vl-app' | 'vl-admin',
  role: UserTypeCode
) => {
  pageType = pageType || 'patients';
  let error;
  try {
    const errorFromResult =
      confirmResult.error?.data ||
      confirmResult.error ||
      (await confirmResult.payload?.json()) ||
      (await confirmResult.error.json());
    error = {
      code: errorFromResult.code || errorFromResult.status,
      message: errorFromResult.message
    };
  } catch (e) {
    console.error('Error parsing error code', e);
    error = { code: 500, message: 'An error occurred. Please try again later.' };
  }
  return formatError(error.code, pageType, systemType, role, error.message);
};

// TODO Remove this component as its not used anymore after [VLNHS-6170] and [VLNHS-6169]
export const ConfirmAppointment = ({
  schedule,
  pageType,
  title,
  history,
  StartNextSurveyModal
}: {
  schedule?: boolean;
  pageType: string;
  title?: string;
  history?: any;
  StartNextSurveyModal?: React.FC<{
    caseId: number | null;
    showModal: boolean;
    setShowModal: (value: boolean) => void;
    continueDataTestid: string;
    title: string;
    noNextSurveyText?: React.ReactNode;
    closeSideEffect?: () => void;
    onCancelModal?: () => void;
    subtitle?: React.ReactNode;
  }>;
}) => {
  const dispatch = useDispatch();
  // this needs to be portable between in vl-app and vl-admin!
  // to - convert to hook
  const queryParams = qs.parse(history?.location.search);

  const { appointment, clearAppointment, updateAppointment } = useContext(AppointmentContext);
  const user = useUser();
  const role = user?.user_type_code;
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [nextSurveyModalVisible, setNextSurveyModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [isPatientBookedAppointment, setIsPatientBookedAppointment] = useState(false);
  const [clinicianSpecAreas, setClinicianSpecAreas] = useState(null);
  const [clinicianCategoryName, setClinicianCategoryName] = useState(null);
  const [clinicianTitle, setClinicianTitle] = useState(null);
  const patientGuid = appointment.userId;
  const [patient, setPatient] = useState<any>();
  const router = useRouter();
  const systemType = useSystemType();
  const { TWILIO_BACKEND_URL } = useRuntimeConfig();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  useEffect(() => {
    const getClinicianData = async (appointment) => {
      const getClinicianData = await dispatch(
        // @ts-ignore - the Typescript compiler wants to see a datetime property on the appointment object
        actions.allClinicians({
          clin_guid: appointment.clinicianId,
          patient_guid: user.user_type_code === 'PATIENT' ? user.user_guid : appointment.userId,
          spec_cat: appointment.clinicianSpecCat
        })
      );
      setClinicianTitle(getClinicianData.payload[0].title);
      // @ts-ignore
      setClinicianSpecAreas(getClinicianData.payload[0].spec_areas);
      setClinicianCategoryName(getClinicianData.payload[0].cat_name);
    };

    if (appointment.patientBookedAppointment) setIsPatientBookedAppointment(true);
    if (appointment.clinicianId) getClinicianData(appointment).then(console.log, console.error);
  }, [dispatch, user, appointment]);

  useEffect(() => {
    if (patientGuid) {
      // @ts-ignore
      dispatch(actions.getPatientDetails(user.user_guid, patientGuid)).then(({ users }) => setPatient(users[0]));
    }
  }, [patientGuid, user?.user_guid /* Api.referrers.getPatient, Api.referrers */]);
  const confirmAppointment = async (item) => {
    let isError = false;
    let confirmResult;
    const {
      area_code,
      isRescheduleFlow,
      case__appointment_id: appointment_id,
      case_ref,
      selectedEligibilityChecks
    } = queryParams;

    setBookingInProgress(true);
    try {
      const tryFindExistingCaseId = async () => {
        // @ts-ignore Ignored because the appointment context is dissimilar between vl-app and vl-admin
        if (appointment?.caseId || appointment?.case__case_id) {
          // @ts-ignore
          return (appointment?.caseId || appointment?.case__case_id) as number;
        }
        // this shim is required because the behaviour (and return value of createCase) is different between vl-app and
        // vl-admin
        const shim = async () => {
          if (systemType === 'vl-app') {
            const r = await dispatch(
              actions.createCase({
                pat_guid: item.userId,
                // @ts-expect-error
                speccat: appointment.clinicianSpecCat,
                specarea: area_code || appointment.patientSpecArea,
                case_ref: case_ref
              })
            );

            // 409 ==> case already exists and the id is returned in the uid property
            if (r.status === 409) {
              return { data: { case_id: r.uid } };
            }

            if (r.status) {
              throw Error(r);
            }

            return { data: r };
          }

          if (item?.case__case_id) {
            return {
              data: {
                case_id: item?.case__case_id
              }
            };
          }

          return dispatch(
            actions.createCase({
              pat_guid: item.userId,
              // @ts-expect-error
              speccat: appointment.clinicianSpecCat,
              specarea: area_code,
              // @ts-expect-error
              caseUrgency: appointment.caseUrgency,
              case_ref: case_ref
            })
          )
            .then((response: { data: { case_id: number } }) => {
              const caseId = response?.data?.case_id;
              updateAppointment({ caseId });
              return { data: { case_id: caseId } };
            })
            .catch((e) => {
              if (e.response?.data) {
                const { code, uid } = e.response.data;

                if (code === 409) {
                  return { data: { case_id: uid } };
                }
              }
              throw e;
            });
        };
        const { data } = await shim();
        const { case_id } = data;

        return case_id as number;
      };

      const case_id = await tryFindExistingCaseId();
      if (appointment.patientBookedAppointment) {
        // this gets executed as part of patient self booking
        const data = {
          user_guid: appointment.userId,
          clin_guid: appointment.clinicianId,
          case_id: case_id || appointment.caseId,
          date: new Date(appointment.date),
          type: appointment.appointmentType || 'NEW',
          call_type: appointment.callType?.toUpperCase()
        };
        const action = actions.scheduleConfirm(data);
        confirmResult = await dispatch(action, false);
      } else if (item?.clinicianRescheduling) {
        const action = actions.rescheduleConfirm({
          user_guid: item.userId,
          appt_id: item.appointmentId,
          date: item.date,
          clin_user_guid: item.clinicianId,
          call_type_code: item.callType
        });
        confirmResult = await dispatch(action, false);
      } else if (schedule && isRescheduleFlow !== 'true') {
        // executed by  vl-admin to book appointment
        const action = actions.scheduleConfirm({
          user_guid: appointment.userId,
          clin_guid: appointment.clinicianId,
          case_id,
          date: new Date(item.date),
          type: appointment.appointmentType,
          call_type: appointment.callType?.toUpperCase() // AUDIO - VIDEO
        });
        confirmResult = await dispatch(action, false);
        const postCaseEligibilityPolicy = async (policyCode: string) =>
          dispatch(
            actions.postEligibilityPolicy({
              caseId: case_id,
              policyCode,
              applied: true
            })
          );
        const arrayOfEligibilityChecks = Array.isArray(selectedEligibilityChecks)
          ? selectedEligibilityChecks
          : [selectedEligibilityChecks];

        await Promise.all(
          arrayOfEligibilityChecks.filter(Boolean).map((policyCode) => postCaseEligibilityPolicy(policyCode))
        );
      } else {
        // executed by  vl-admin appointment reschedule
        const action = actions.rescheduleConfirm({
          user_guid: item.userId,
          appt_id: appointment_id || item.appointmentId,
          date: new Date(item.date),
          clin_user_guid: item.clinicianId,
          call_type_code: item.callType,
          reason: queryParams.reason
        });
        confirmResult = await dispatch(action, false);
        const isDifferentClinician = item.clinicianId !== queryParams.case__clinician_user_guid;
        if (isDifferentClinician && confirmResult.type === 'RESCHEDULE_CONFIRM_SUCCESS') {
          const message = {
            clinicianChangedFrom: queryParams.case__clinician_user_guid,
            clinicianChangedTo: item.clinicianId,
            newClinicianName: `${clinicianTitle} ${item.clinician}`
          };
          await sendToParticipants(
            appointment_id,
            message,
            TWILIO_BACKEND_URL,
            queryParams.case__clinician_user_guid,
            queryParams.patient__user_guid
          );
        }
      }

      if (confirmResult.type === 'RESCHEDULE_CONFIRM_SUCCESS') {
        if (role === 'PATIENT' && StartNextSurveyModal) {
          setNextSurveyModalVisible(true);
        } else {
          setConfirmModalVisible(true);
        }
      } else {
        isError = true;
      }
    } catch (e) {
      isError = true;
      console.error('Unknown error booking appointment', e);
      confirmResult = { code: 500, message: 'An error occurred. Please try again later.' };
    } finally {
      if (isError) {
        const { message, redirectTo } = await getError(confirmResult, pageType, systemType, role);
        errorModal({
          title: 'Error',
          content: message,
          onOk: () => router.push(redirectTo),
          okText: 'Ok'
        });
      }
      setBookingInProgress(false);

      queryClient.invalidateQueries({
        queryKey: baseUseClinicianListQueryKey
      });
      queryClient.invalidateQueries({
        queryKey: baseGetSlotBucketsQueryKey
      });

      queryClient.invalidateQueries({
        queryKey: ['userAppointment']
      });
      queryClient.invalidateQueries({
        queryKey: ['get-direct-book-referrals']
      });
      queryClient.invalidateQueries({
        queryKey: ['get-active-cases']
      });
      queryClient.invalidateQueries({
        queryKey: ['useUserNextAppointments']
      });
      queryClient.invalidateQueries({
        queryKey: ['clinicianAppointment']
      });
    }
  };

  const reRouteToAppointmentsPage = () => {
    const apptsRoute = {
      CLIENTADMIN: '/admin/appointments',
      ADMIN: '/referrer-admin/ReferrerAppointments',
      REFERRER: '/referrer/ReferrerAppointments'
    }[user?.user_type_code];

    const tasksRoute = {
      CLIENTADMIN: '/admin/tasks',
      ADMIN: '/referrer-admin/ReferrerTasks',
      REFERRER: '/referrer/ReferrerTasks'
    }[user?.user_type_code];

    const { returnRoute } = queryParams;
    const route = returnRoute === 'tasks' ? tasksRoute : apptsRoute;

    if (systemType === 'vl-admin') {
      history.push(route);
    } else {
      router.push(`/${pageType}/appointments`);
    }
  };

  const cancelAppointment = async () => {
    setCancelModalVisible(false);
    reRouteToAppointmentsPage();
  };

  const bookedSuccess = async () => {
    clearAppointment();
    reRouteToAppointmentsPage();
  };

  return (
    <ConfirmationContainer>
      <div className="confirmation-appointment">
        {title && <h1 className="confirmation-title">{title}</h1>}

        <div className="confirmation-list">
          <Card className="confirmation">
            <Row className="title-row">
              <h2>{t('Patient Details')}</h2>
            </Row>
            <Row className="data-row">
              <Col span={4} className="icon-col">
                <ProfileImage user={appointment?.userId || ''} width="50" height="50" />
              </Col>
              <Col span={20}>
                <Row>
                  <p>
                    <b>{t('Patient: ')}</b> {patient ? `${patient.title} ${patient.full_name}` : ''}
                  </p>
                </Row>
                <Row>
                  <p>
                    <b>{t('Area of complaint:')}</b>{' '}
                    {capitalize(appointment?.patientSpecArea || undefined) || clinicianCategoryName}
                  </p>
                </Row>
              </Col>
            </Row>
          </Card>

          <Card className="confirmation">
            <Row className="title-row">
              <h2>{t('Date and time')}</h2>
            </Row>
            <Row className="data-row">
              <Col span={4} className="icon-col">
                <FaRegCalendarAlt className="icon-item" />
              </Col>
              <Col span={20}>
                <Row>
                  <p>
                    <b>{t('Date')}:</b>{' '}
                    <Datestamp
                      date={new Date(appointment?.date)}
                      format="Long Date with day-of-week - Thursday, March 3rd, 2022"
                      timezone={user?.timezone}
                    />
                  </p>
                </Row>
                <Row>
                  <p>
                    <b>{t('Time')}:</b>{' '}
                    <Datestamp
                      date={new Date(appointment?.date)}
                      format="24 hour time - 09:47"
                      timezone={user?.timezone}
                    />
                  </p>
                </Row>
                <Row>
                  <p>
                    <b>{t('Call Type:')}</b>{' '}
                    {appointment?.callType === 'AUDIO' ? t('Phone appointment') : t('Video appointment')}
                  </p>
                </Row>
              </Col>
            </Row>
          </Card>

          {appointment?.clinicianId && (
            <Card className="confirmation">
              <Row className="title-row">
                <h2>{t('Clinician')}</h2>
              </Row>
              <Row className="data-row">
                <Col span={4} className="icon-col">
                  <ProfileImage user={appointment?.clinicianId || user.user_guid} width="50" height="50" />
                </Col>
                <Col span={20}>
                  <Row>
                    {/* @ts-ignore */}
                    <p className="item-title">{appointment?.clinician}</p>
                  </Row>
                  <Row>
                    {/* @ts-ignore */}
                    <p>{appointment?.clinicianType}</p>
                  </Row>
                  <Row>
                    <p>
                      <b>{t('Specialised areas:')}</b> {clinicianSpecAreas || appointment?.clinicianAreas}
                    </p>
                  </Row>
                </Col>
              </Row>
            </Card>
          )}
        </div>

        <div className="confirm-button">
          <Button onClick={() => setCancelModalVisible(true)} data-testid="cancel-button">
            {t('Cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() => confirmAppointment(appointment)}
            disabled={bookingInProgress}
            data-testid="confirm-and-book-button"
          >
            {t('Confirm and Book')}
          </Button>
        </div>
        {StartNextSurveyModal && (
          <StartNextSurveyModal
            showModal={nextSurveyModalVisible}
            caseId={appointment?.caseId ?? null}
            setShowModal={setNextSurveyModalVisible}
            continueDataTestid="continue-to-survey"
            title={t('All booked')}
            closeSideEffect={clearAppointment}
            noNextSurveyText=""
          />
        )}

        <SuccessModal
          title={t('All booked')}
          open={confirmModalVisible}
          onCancel={() => bookedSuccess().then(() => setConfirmModalVisible(false))}
          cancelText="Return to dashboard"
          data-testid="return-to-dashboard"
        />

        <ConfirmationModal
          title="Cancel appointment"
          open={cancelModalVisible}
          className="cancel-modal"
          onOk={() => cancelAppointment()}
          onCancel={() => setCancelModalVisible(false)}
        >
          <section className="text-center">
            <p>
              {`Are you sure you wish to cancel
                ${isPatientBookedAppointment ? 'scheduling' : 'rescheduling'}
              this appointment? You will lose all the data saved so far.`}
            </p>
          </section>
        </ConfirmationModal>
      </div>
    </ConfirmationContainer>
  );
};

export default ConfirmAppointment;
